import { request } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/** Loại tài khoản đăng nhập. */
export const UserType = {
  /** Nhân viên công ty. */
  Employee: 1,

  /** Cộng tác viên hoặc đơn vị thuê ngoài, ví dụ kế toán dịch vụ. */
  Contractor: 2,

  /** Tài khoản dành cho tiến trình tích hợp, không phải con người. */
  SystemAccount: 3,

  /** Người dùng phía khách hàng, đăng nhập qua cổng thông tin khách hàng. */
  CustomerPortal: 4,
} as const;

/**
 * Thông tin một tài khoản.
 *
 * Không có trường nào chứa mật khẩu, chuỗi băm hay bí mật xác thực hai lớp — máy chủ không trả về
 * chúng. `hasPassword` là cờ cho biết tài khoản đã đăng nhập được chưa.
 */
export interface UserAccount {
  readonly id: string;
  readonly userName: string;
  readonly email: string;
  readonly fullName: string;
  readonly userType: number;
  readonly isActive: boolean;
  readonly deactivationReason: string | null;
  readonly mustChangePassword: boolean;
  readonly hasPassword: boolean;
  readonly isLockedOutPermanently: boolean;
  readonly lockoutEndAt: string | null;
  readonly failedAccessCount: number;
  readonly lastLoginAt: string | null;
  readonly isTwoFactorEnabled: boolean;
  readonly preferredLanguageCode: string;

  /** Mã những vai trò CÒN HIỆU LỰC hôm nay. Dùng cho cột "Vai trò" ở danh sách. */
  readonly roleCodes: readonly string[];

  /**
   * Mọi phép gán vai trò của tài khoản, **kể cả phép gán đã hết hạn** và vai trò đã tắt "Gán được".
   *
   * Hộp quản lý vai trò dựng bảng từ danh sách này chứ không từ `roleCodes`: nó cần `roleId` thật để
   * thu hồi, và cần thấy phép gán đã hết hạn để gán lại. Trước đây bảng ghép `roleCodes` với danh sách
   * vai trò gán được, nên vai trò đã tắt "Gán được" ra định danh rỗng và không thu hồi được.
   */
  readonly roleAssignments: readonly UserRoleAssignment[];
}

/** Một phép gán vai trò của tài khoản. */
export interface UserRoleAssignment {
  readonly roleId: string;
  readonly roleCode: string;
  readonly roleName: string;

  /** Ngày bắt đầu hiệu lực, dạng YYYY-MM-DD. */
  readonly validFrom: string;

  /** Ngày hết hiệu lực, dạng YYYY-MM-DD. Rỗng nghĩa là lâu dài. */
  readonly validTo: string | null;

  /**
   * Phép gán có hiệu lực hôm nay.
   *
   * Máy chủ tính theo múi giờ nghiệp vụ của nó. Giao diện dùng thẳng cờ này thay vì tự so ngày, để
   * không lệch một ngày quanh nửa đêm khi đồng hồ máy người dùng khác múi giờ.
   */
  readonly isEffective: boolean;
}

/**
 * Kết quả của một lần lập tài khoản hoặc đặt lại mật khẩu.
 *
 * `temporaryPassword` hiện **đúng một lần** và không lấy lại được: máy chủ chỉ lưu chuỗi băm. Giao
 * diện phải nói rõ điều đó trước khi hiển thị, và phải buộc người quản trị xác nhận đã lưu lại.
 */
export interface UserAccountWithTemporaryPassword {
  readonly account: UserAccount;
  readonly temporaryPassword: string;
}

/** Thông tin một vai trò, dùng cho ô chọn vai trò. */
export interface Role {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly isSystemRole: boolean;
  readonly isAssignable: boolean;
  readonly description: string | null;
}

/** Điều kiện lọc danh sách tài khoản. */
export interface UserAccountFilter {
  readonly searchTerm?: string;
  readonly userType?: number;
  readonly includeInactive?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/**
 * Nội dung gửi lên khi lập tài khoản mới.
 *
 * Không có trường mật khẩu: máy chủ tự sinh mật khẩu tạm. Nhận mật khẩu từ giao diện sẽ mở đường cho
 * việc đặt cùng một mật khẩu dễ đoán cho cả phòng, và mật khẩu đó đi qua nhật ký của mọi tầng trung
 * gian trên đường lên máy chủ.
 */
export interface CreateUserAccountPayload {
  readonly userName: string;
  readonly email: string;
  readonly fullName: string;
  readonly userType: number;
}

/** Nội dung gửi lên khi sửa họ tên và ngôn ngữ giao diện. */
export interface UpdateUserAccountProfilePayload {
  readonly fullName: string;
  readonly preferredLanguageCode: string;
}

/** Nội dung gửi lên khi gán vai trò. */
export interface AssignRoleToUserPayload {
  readonly roleId: string;
  readonly validTo: string | null;
  readonly assignmentReason: string | null;
}

const baseRoute = '/api/v1/platform/user-accounts';

/** Dựng phần truy vấn của địa chỉ, bỏ hẳn tham số rỗng. */
function buildQueryString(filter: UserAccountFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.userType !== undefined) {
    parameters.set('userType', String(filter.userType));
  }

  if (filter.includeInactive === true) {
    parameters.set('includeInactive', 'true');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách tài khoản. */
export async function listUserAccounts(
  filter: UserAccountFilter,
): Promise<PagedResponse<UserAccount>> {
  return await request<PagedResponse<UserAccount>>(
    `${baseRoute}${buildQueryString(filter)}`,
  );
}

/** Lấy danh sách vai trò gán được cho người thật. */
export async function listAssignableRoles(): Promise<readonly Role[]> {
  return await request<readonly Role[]>(`${baseRoute}/roles`);
}

/** Lấy chi tiết một tài khoản. */
export async function getUserAccount(id: string): Promise<UserAccount> {
  return await request<UserAccount>(`${baseRoute}/${id}`);
}

/** Lập một tài khoản mới. Trả về mật khẩu tạm hiện đúng một lần. */
export async function createUserAccount(
  payload: CreateUserAccountPayload,
): Promise<UserAccountWithTemporaryPassword> {
  return await request<UserAccountWithTemporaryPassword>(baseRoute, {
    method: 'POST',
    body: payload,
  });
}

/** Sửa họ tên và ngôn ngữ giao diện. */
export async function updateUserAccountProfile(
  id: string,
  payload: UpdateUserAccountProfilePayload,
): Promise<UserAccount> {
  return await request<UserAccount>(`${baseRoute}/${id}/profile`, {
    method: 'PUT',
    body: payload,
  });
}

/** Đổi thư điện tử của tài khoản. */
export async function changeUserAccountEmail(
  id: string,
  email: string,
): Promise<UserAccount> {
  return await request<UserAccount>(`${baseRoute}/${id}/email`, {
    method: 'PUT',
    body: { email },
  });
}

/** Vô hiệu hóa hoặc kích hoạt lại tài khoản. */
export async function setUserAccountActive(
  id: string,
  isActive: boolean,
  reason: string | null,
): Promise<UserAccount> {
  return await request<UserAccount>(`${baseRoute}/${id}/active`, {
    method: 'PUT',
    body: { isActive, reason },
  });
}

/**
 * Đặt lại mật khẩu của một tài khoản khác. Trả về mật khẩu tạm hiện đúng một lần.
 *
 * Máy chủ từ chối khi tài khoản là chính người đang đăng nhập — đổi mật khẩu của mình thì đi qua
 * trang đổi mật khẩu, nơi phải nhập mật khẩu cũ.
 */
export async function resetUserAccountPassword(
  id: string,
): Promise<UserAccountWithTemporaryPassword> {
  return await request<UserAccountWithTemporaryPassword>(
    `${baseRoute}/${id}/reset-password`,
    { method: 'POST', body: {} },
  );
}

/**
 * Gán vai trò cho tài khoản.
 *
 * Gán một vai trò mà tài khoản từng có nhưng đã hết hạn thì máy chủ mở lại phép gán cũ, không lập
 * phép gán thứ hai.
 */
export async function assignRoleToUser(
  id: string,
  payload: AssignRoleToUserPayload,
): Promise<UserAccount> {
  return await request<UserAccount>(`${baseRoute}/${id}/roles`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Thu hồi vai trò khỏi tài khoản.
 *
 * Máy chủ trả về tài khoản sau khi thu hồi, nên hàm này trả về nó thay vì không trả gì — nơi gọi cập
 * nhật được danh sách vai trò ngay mà không phải gọi thêm một lượt.
 *
 * Máy chủ từ chối thu hồi vai trò của chính người đang đăng nhập: đó là cách tự tước quyền quản trị
 * của mình mà không ai khác biết để gán lại.
 */
export async function revokeRoleFromUser(
  id: string,
  roleId: string,
): Promise<UserAccount> {
  return await request<UserAccount>(`${baseRoute}/${id}/roles/${roleId}`, {
    method: 'DELETE',
  });
}
